import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { exchangeTelegramOidcCode } from '@/api/auth';
import { useT } from '@/contexts/I18nContext';
import {
  TG_OIDC_NEXT_KEY,
  TG_OIDC_STATE_KEY,
  TG_OIDC_VERIFIER_KEY,
} from '@/components/Auth/TelegramLoginButton';

/** /auth/tg/callback — landing page from oauth.telegram.org/auth.
 *
 *  Reads ?code= and ?state= from the URL, verifies state matches the
 *  value stashed before the redirect, exchanges the code at the BE
 *  for a JWT, then sends the user to whatever path was active when
 *  they kicked off the flow. */
const TgOidcCallbackPage = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    const oauthError = url.searchParams.get('error');

    if (oauthError) {
      // TG redirected with ?error= (user denied consent, bad config, etc).
      const desc = url.searchParams.get('error_description') || oauthError;
      setError(desc);
      return;
    }
    if (!code || !state) {
      setError(t('auth.oidcMissingCode'));
      return;
    }

    const expectedState = sessionStorage.getItem(TG_OIDC_STATE_KEY);
    const verifier = sessionStorage.getItem(TG_OIDC_VERIFIER_KEY);
    const next = sessionStorage.getItem(TG_OIDC_NEXT_KEY) || '/';
    if (!expectedState || !verifier || expectedState !== state) {
      // State mismatch — either the user refreshed mid-flow, opened
      // the callback in a different tab, or this is a forged callback.
      // Don't proceed.
      setError(t('auth.oidcStateMismatch'));
      return;
    }

    // Same redirect_uri the FE sent at /authorize time. TG checks this
    // matches at /token; building it the same way (from origin) keeps
    // it in sync without smuggling it through sessionStorage too.
    const redirectUri = `${window.location.origin}/auth/tg/callback`;

    // If we came from an invite link (next was /invite/<token>), pass
    // the token to the BE so it can bootstrap an account for the TG
    // user — without this the unbound TG user would hit 403 even
    // though the invite is meant to authorize their access.
    const inviteMatch = next.match(/^\/invite\/([^/?#]+)/);
    const inviteToken = inviteMatch ? inviteMatch[1] : undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await exchangeTelegramOidcCode({
          code,
          code_verifier: verifier,
          redirect_uri: redirectUri,
          ...(inviteToken ? { invite_token: inviteToken } : {}),
        });
        if (cancelled) return;
        localStorage.setItem('jwt', res.data.access);
        if (res.data.refresh) localStorage.setItem('refresh', res.data.refresh);
        sessionStorage.removeItem(TG_OIDC_STATE_KEY);
        sessionStorage.removeItem(TG_OIDC_VERIFIER_KEY);
        sessionStorage.removeItem(TG_OIDC_NEXT_KEY);
        window.location.href = next;
      } catch (e: unknown) {
        const err = e as {
          response?: { status?: number; data?: { detail?: string; tg?: unknown } };
          message?: string;
        };
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        // Surface details to console so failed sign-ins are
        // diagnosable from the browser without server logs.
        console.error('[TgOidcCallback] exchange failed', { status, detail, body: err?.response?.data });
        if (status === 403 && detail === 'no_binding') {
          setError(t('auth.tgNoBinding'));
        } else if (status === 409 && detail === 'tg_identity_changed') {
          // The handle is bound + confirmed, but the Telegram sign-in
          // identity changed (e.g. a different bot), so the stored link
          // no longer matches. An admin re-bind clears it.
          setError(t('auth.tgIdentityChanged'));
        } else if (status === 409) {
          // BE returns this when a TG identity is already bound to a
          // different Django user than the one trying to bind.
          setError(t('auth.tgAlreadyBound'));
        } else if (status && detail) {
          setError(`${detail} (HTTP ${status})`);
        } else if (status) {
          setError(`${t('auth.oidcExchangeFailed')} (HTTP ${status})`);
        } else {
          // No HTTP response at all — likely a network / CORS issue.
          setError(`${t('auth.oidcExchangeFailed')} (${err?.message || 'network error'})`);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, t]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        {error ? (
          <>
            <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
            <button
              type="button"
              onClick={() => { window.location.href = '/login'; }}
              className="text-sm text-primary underline-offset-2 hover:underline"
            >
              {t('auth.backToLogin')}
            </button>
          </>
        ) : (
          <>
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('auth.oidcExchanging')}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default TgOidcCallbackPage;
